import {
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { type ComponentType, type FC, type SVGProps, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";

import { RioEditTone, type RioEditToneValue } from "@pages/Templates/constants";

import { type RioMessage, ToolNameEnum, humanizeToolName } from "./rioMessages";

interface RioEditsListProps {
  edits: RioMessage[];
}

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Human-readable description for one persisted edit row.
 *
 * Uses values from `args` (for additions/updates/moves) and
 * `pre_delete_state` (for removals) so the sentence reflects what the
 * user can actually identify in the UI (titles, not snake_case keys
 * where possible). Falls back to whatever identifier is available if
 * the title isn't in the captured payload.
 */
function describe(msg: RioMessage): { Icon: IconType; sentence: string; tone: RioEditToneValue } | null {
  if (!msg.edit) return null;
  const { tool_name, args, result_summary, pre_delete_state, pre_update_state, section_title, new_section_title } =
    msg.edit;
  const a = (args ?? {}) as Record<string, unknown>;
  const p = (pre_delete_state ?? {}) as Record<string, unknown>;
  const u = (pre_update_state ?? {}) as Record<string, unknown>;

  const str = (v: unknown, fallback = ""): string => (typeof v === "string" && v.length > 0 ? v : fallback);
  const num = (v: unknown): number | null => (typeof v === "number" ? v : null);

  // Prefer the human-readable title resolved against the current template
  // state. Falls back to the snake_case key if the section was deleted /
  // doesn't exist yet (e.g. immediately after an apply_add_section).
  const sectionLabel = section_title || str(a.section_key, "section");
  const newSectionLabel = new_section_title || str(a.new_section_key);

  switch (tool_name) {
    case ToolNameEnum.apply_add_section: {
      // For adds, args.title is the user-supplied display title — use it
      // directly (no current-state lookup needed since the row was just
      // created with this title).
      const label = str(a.title) || str(a.key, "(untitled)");
      return { Icon: PlusIcon, sentence: `Added section ‘${label}’`, tone: RioEditTone.ADD };
    }
    case ToolNameEnum.apply_update_section: {
      const newTitle = str(a.title);
      const newKey = str(a.new_key);
      // Before-rename label priority:
      //   1. pre_update_state.title — captured at the moment of the rename,
      //      always correct when present.
      //   2. args.section_key — often the same as the pre-rename title
      //      for templates where Rio auto-generated keys from titles.
      //   3. sectionLabel (current title) — last-resort; produces
      //      "X → X" for old rows but we have nothing better.
      const beforeTitle = str(u.title) || str(a.section_key) || sectionLabel;
      if (newTitle) {
        return {
          Icon: PencilSquareIcon,
          sentence: `Renamed section ‘${beforeTitle}’ → ‘${newTitle}’`,
          tone: RioEditTone.UPDATE,
        };
      }
      if (newKey) {
        return {
          Icon: PencilSquareIcon,
          sentence: `Renamed section key ‘${str(u.key) || sectionLabel}’ → ‘${newKey}’`,
          tone: RioEditTone.UPDATE,
        };
      }
      return { Icon: PencilSquareIcon, sentence: `Updated section ‘${sectionLabel}’`, tone: RioEditTone.UPDATE };
    }
    case ToolNameEnum.apply_remove_section: {
      // For removes, pre_delete_state.title is the title AT THE TIME of
      // deletion — accurate even if other sections have been renamed since.
      const title = str(p.title) || sectionLabel || "(unknown)";
      const fields = Array.isArray(p.fields) ? (p.fields as unknown[]).length : 0;
      const tail = fields === 0 ? "" : ` (${fields} field${fields === 1 ? "" : "s"})`;
      return { Icon: TrashIcon, sentence: `Removed section ‘${title}’${tail}`, tone: RioEditTone.REMOVE };
    }
    case ToolNameEnum.apply_move_section: {
      const pos = num(a.new_rank_order);
      return {
        Icon: ArrowsUpDownIcon,
        sentence:
          pos !== null ? `Moved section ‘${sectionLabel}’ to position ${pos}` : `Moved section ‘${sectionLabel}’`,
        tone: RioEditTone.MOVE,
      };
    }
    case ToolNameEnum.apply_add_field: {
      const label = str(a.title) || str(a.field_identifier, "(untitled)");
      const fieldType = str(a.field_type);
      const typePart = fieldType ? ` (${fieldType})` : "";
      return {
        Icon: PlusIcon,
        sentence: `Added field ‘${label}’${typePart} to ‘${sectionLabel}’`,
        tone: RioEditTone.ADD,
      };
    }
    case ToolNameEnum.apply_update_field: {
      const id = str(a.field_identifier, "field");
      const newId = str(a.new_field_identifier);
      const newTitle = str(a.title);
      const newType = str(a.field_type);
      // Prefer the OLD field title from pre_update_state. Falls back to
      // the identifier for rows missing the capture (older rows or when
      // title wasn’t being changed).
      const beforeTitle = str(u.title) || id;
      if (newTitle) {
        return {
          Icon: PencilSquareIcon,
          sentence: `Renamed field ‘${beforeTitle}’ → ‘${newTitle}’ in ‘${sectionLabel}’`,
          tone: RioEditTone.UPDATE,
        };
      }
      if (newId) {
        return {
          Icon: PencilSquareIcon,
          sentence: `Renamed field ‘${str(u.field_identifier) || id}’ → ‘${newId}’ in ‘${sectionLabel}’`,
          tone: RioEditTone.UPDATE,
        };
      }
      if (newType) {
        const oldType = str(u.field_type);
        const change = oldType ? `from ${oldType} to ${newType}` : `to ${newType}`;
        return {
          Icon: PencilSquareIcon,
          sentence: `Changed ‘${id}’ type ${change} in ‘${sectionLabel}’`,
          tone: RioEditTone.UPDATE,
        };
      }
      return {
        Icon: PencilSquareIcon,
        sentence: `Updated field ‘${id}’ in ‘${sectionLabel}’`,
        tone: RioEditTone.UPDATE,
      };
    }
    case ToolNameEnum.apply_remove_field: {
      const title = str(p.title) || str(a.field_identifier, "(unknown)");
      return {
        Icon: TrashIcon,
        sentence: `Removed field ‘${title}’ from ‘${sectionLabel}’`,
        tone: RioEditTone.REMOVE,
      };
    }
    case ToolNameEnum.apply_move_field: {
      const id = str(a.field_identifier, "field");
      const pos = num(a.new_rank_order);
      if (newSectionLabel) {
        const tail = pos !== null ? ` at position ${pos}` : "";
        return {
          Icon: ArrowsRightLeftIcon,
          sentence: `Moved field ‘${id}’ to ‘${newSectionLabel}’${tail}`,
          tone: RioEditTone.MOVE,
        };
      }
      return {
        Icon: ArrowsUpDownIcon,
        sentence: pos !== null ? `Moved field ‘${id}’ to position ${pos}` : `Moved field ‘${id}’`,
        tone: RioEditTone.MOVE,
      };
    }
    case ToolNameEnum.apply_update_template_description: {
      return { Icon: DocumentTextIcon, sentence: "Updated template description", tone: RioEditTone.UPDATE };
    }
    case ToolNameEnum.apply_add_treatment_plan_domain: {
      return { Icon: SparklesIcon, sentence: "Added treatment-plan domain", tone: RioEditTone.ADD };
    }
    case ToolNameEnum.apply_update_treatment_plan_domain: {
      return { Icon: DocumentTextIcon, sentence: "Updated treatment-plan domain", tone: RioEditTone.UPDATE };
    }
    case ToolNameEnum.apply_remove_treatment_plan_domain: {
      return { Icon: DocumentTextIcon, sentence: "Removed treatment-plan domain", tone: RioEditTone.REMOVE };
    }
    case ToolNameEnum.apply_add_goal_field: {
      return { Icon: SparklesIcon, sentence: "Added goal field", tone: RioEditTone.ADD };
    }
    case ToolNameEnum.apply_update_goal_field: {
      return { Icon: DocumentTextIcon, sentence: "Updated goal field", tone: RioEditTone.UPDATE };
    }
    case ToolNameEnum.apply_remove_goal_field: {
      return { Icon: DocumentTextIcon, sentence: "Removed goal field", tone: RioEditTone.REMOVE };
    }
    case ToolNameEnum.apply_add_target_field: {
      return { Icon: SparklesIcon, sentence: "Added target field", tone: RioEditTone.ADD };
    }
    case ToolNameEnum.apply_update_target_field: {
      return { Icon: DocumentTextIcon, sentence: "Updated target field", tone: RioEditTone.UPDATE };
    }
    case ToolNameEnum.apply_remove_target_field: {
      return { Icon: DocumentTextIcon, sentence: "Removed target field", tone: RioEditTone.REMOVE };
    }
    default:
      return {
        Icon: SparklesIcon,
        sentence: result_summary || humanizeToolName(tool_name),
        tone: RioEditTone.NEUTRAL,
      };
  }
}

// Per-tone color treatment. Each row gets:
//   - a small colored circle around the icon (visual anchor when scanning)
//   - a 2px tinted left border (groups similar actions vertically when
//     several land in a row)
// Background of the row itself stays neutral so the panel doesn't feel busy.
const TONE_STYLES: Record<RioEditToneValue, { iconBg: string; iconText: string; leftBorder: string; chip: string }> = {
  [RioEditTone.ADD]: {
    iconBg: "bg-green-50",
    iconText: "text-green-600",
    leftBorder: "border-l-green-500",
    chip: "Added",
  },
  [RioEditTone.UPDATE]: {
    iconBg: "bg-amber-50",
    iconText: "text-amber-600",
    leftBorder: "border-l-amber-500",
    chip: "Updated",
  },
  [RioEditTone.REMOVE]: {
    iconBg: "bg-red-50",
    iconText: "text-red-600",
    leftBorder: "border-l-red-500",
    chip: "Removed",
  },
  [RioEditTone.MOVE]: {
    iconBg: "bg-blue-50",
    iconText: "text-blue-600",
    leftBorder: "border-l-blue-500",
    chip: "Moved",
  },
  [RioEditTone.NEUTRAL]: {
    iconBg: "bg-bg-default",
    iconText: "text-text-secondary",
    leftBorder: "border-l-border-subtle",
    chip: "Edit",
  },
};

/** Format a value for display in the diff. JSON-stringifies non-strings;
 * leaves strings as-is so multi-line content stays readable. */
function formatDiffValue(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "(none)";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

/** Map a pre_update_state key to its corresponding new-value key in args.
 * Most are direct; identifiers and section keys get a `new_` prefix at
 * the tool boundary (see apply_update_section / apply_update_field). */
function argsKeyFor(preUpdateKey: string): string {
  if (preUpdateKey === "field_identifier") return "new_field_identifier";
  if (preUpdateKey === "key") return "new_key";
  return preUpdateKey;
}

const UpdateDiff: FC<{
  preUpdate: Record<string, unknown>;
  args: Record<string, unknown>;
}> = ({ preUpdate, args }) => {
  const entries = Object.entries(preUpdate);
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-2 rounded bg-bg-default px-2 py-2">
      {entries.map(([key, oldVal]) => {
        const newVal = args[argsKeyFor(key)] ?? args[key];
        const isContent = key === "content";
        return (
          <div key={key} className="text-xs">
            <div className="mb-0.5 font-medium text-text-secondary">{key}</div>
            <div className="space-y-1">
              <div className="rounded border border-red-200 bg-red-50 px-1.5 py-1 text-red-900">
                <span className="select-none text-red-400">− </span>
                <span className={isContent ? "whitespace-pre-wrap break-words" : "break-words"}>
                  {formatDiffValue(oldVal)}
                </span>
              </div>
              <div className="rounded border border-green-200 bg-green-50 px-1.5 py-1 text-green-900">
                <span className="select-none text-green-400">+ </span>
                <span className={isContent ? "whitespace-pre-wrap break-words" : "break-words"}>
                  {formatDiffValue(newVal)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EditRow: FC<{ msg: RioMessage }> = ({ msg }) => {
  const [expanded, setExpanded] = useState(false);
  const described = describe(msg);
  if (!described) return null;
  const { Icon, sentence, tone } = described;
  const styles = TONE_STYLES[tone];
  const hasDeleteDetails = !!msg.edit?.pre_delete_state;
  const hasUpdateDetails = !!msg.edit?.pre_update_state;
  const hasDetails = hasDeleteDetails || hasUpdateDetails;

  return (
    <li className={`rounded-md border border-border-subtle border-l-4 bg-bg-surface px-3 py-2 ${styles.leftBorder}`}>
      <button
        type="button"
        onClick={() => hasDetails && setExpanded((e) => !e)}
        className={`flex w-full items-center gap-2.5 text-left ${hasDetails ? "cursor-pointer" : "cursor-default"}`}
        disabled={!hasDetails}
        aria-expanded={hasDetails ? expanded : undefined}
      >
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
          aria-hidden
        >
          <Icon className={`h-4 w-4 ${styles.iconText}`} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-text-primary">{sentence}</div>
        </div>
        {hasDetails && (
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 text-text-secondary transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {expanded && hasDeleteDetails && (
        <pre className="mt-2 max-h-64 overflow-auto rounded bg-bg-default px-2 py-1.5 text-xs text-text-secondary whitespace-pre-wrap break-words">
          {JSON.stringify(msg.edit?.pre_delete_state, null, 2)}
        </pre>
      )}
      {expanded && hasUpdateDetails && (
        <UpdateDiff
          preUpdate={msg.edit?.pre_update_state as Record<string, unknown>}
          args={(msg.edit?.args ?? {}) as Record<string, unknown>}
        />
      )}
    </li>
  );
};

export const RioEditsList: FC<RioEditsListProps> = ({ edits }) => {
  if (edits.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <Small className="text-text-secondary max-w-[280px]">
          Rio hasn&apos;t made any edits to this template yet. Once you ask Rio to make changes, they&apos;ll show up
          here.
        </Small>
      </div>
    );
  }
  // Chronological order (oldest first) — server returns rows in
  // `created_date` ASC, so we render them as-is. Reading top-to-bottom
  // matches how the edits happened in the session.
  return (
    <ul className="flex flex-col gap-2">
      {edits.map((m) => (
        <EditRow key={m.message_id} msg={m} />
      ))}
    </ul>
  );
};
